import Logger from '@/utils/logger'
import { 
  getUserList as apiGetUserList,
  getUserDetail as apiGetUserDetail,
  getUserProfile as apiGetUserProfile,
  updateUser as apiUpdateUser,
  updateUserStatus as apiUpdateUserStatus,
  deleteUser as apiDeleteUser,
  updateUserCreditLimit as apiUpdateUserCreditLimit
} from '@/api/users'
import {
  validateApiResponse,
  validateUserListData,
  getErrorMessage,
  isNetworkError,
  isServerUnavailable,
  getRetryMessage
} from '@/utils/apiValidator'

/**
 * 适配用户数据字段，将接口返回的驼峰命名转换为下划线命名
 * @param {Object} user - 原始用户数据
 * @returns {Object} 适配后的用户数据
 */
function adaptUserData(user) {
  if (!user) return user
  
  return {
    ...user,
    // 头像字段适配
    avatar_url: user.avatarUrl || user.avatar_url || '/static/default-avatar.png',
    // 真实姓名字段适配
    real_name: user.realName || user.real_name,
    // 时间字段适配
    created_at: user.createdAt || user.created_at,
    updated_at: user.updatedAt || user.updated_at,
    superid: user.superid || user.superId || user.super_id || null,
    // 信用额度相关字段
    credit_limit: user.creditLimit || user.credit_limit || 0,
    current_house_count: user.currentHouseCount || user.current_house_count || 0,
    remaining_count: user.remainingCount || user.remaining_count || 0,
    // 确保必要字段存在
    nickname: user.nickname || '未设置昵称',
    phone: user.phone || '',
    city: user.city || '',
    status: user.status || 'active',
    // 统计字段（如果API返回中没有，设置默认值）
    house_count: user.house_count || user.houseCount || 0,
    favorite_count: user.favorite_count || user.favoriteCount || 0
  }
}

const state = {
  userList: [],
  currentUser: null,
  loading: false,
  error: null,
  retryCount: 0,
  pagination: {
    page: 1,
    size: 20,
    total: 0
  }
}

const mutations = {
  SET_USER_LIST(state, { list, pagination }) {
    state.userList = list
    state.pagination = { ...state.pagination, ...pagination }
    state.error = null
  },
  SET_CURRENT_USER(state, user) {
    state.currentUser = user
    state.error = null
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  CLEAR_ERROR(state) {
    state.error = null
  },
  SET_RETRY_COUNT(state, count) {
    state.retryCount = count
  },
  UPDATE_USER_IN_LIST(state, updatedUser) {
    const index = state.userList.findIndex(user => user.id === updatedUser.id)
    if (index !== -1) {
      state.userList.splice(index, 1, updatedUser)
    }
  },
  REMOVE_USER_FROM_LIST(state, userId) {
    const index = state.userList.findIndex(user => user.id === userId)
    if (index !== -1) {
      state.userList.splice(index, 1)
    }
  }
}

const actions = {
  async fetchUsers({ commit }, params) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const response = await apiGetUserList(params)
      
      // 验证API响应结构
      const responseValidation = validateApiResponse(response)
      if (!responseValidation.isValid) {
        const error = new Error(responseValidation.error)
        error.type = 'VALIDATION_ERROR'
        throw error
      }
      
      // 检查响应状态码
      if (response.code !== 200) {
        const errorMessage = getErrorMessage(response.code, response.msg || '获取用户列表失败')
        const error = new Error(errorMessage)
        error.type = 'API_ERROR'
        error.code = response.code
        throw error
      }
      
      // 验证用户列表数据结构
      const dataValidation = validateUserListData(response.data)
      if (!dataValidation.isValid) {
        const error = new Error(dataValidation.error)
        error.type = 'DATA_VALIDATION_ERROR'
        throw error
      }
      
      // 适配用户列表数据字段
      const adaptedList = response.data.list.map(adaptUserData)

      commit('SET_USER_LIST', {
        list: adaptedList,
        pagination: {
          page: response.data.page,
          size: response.data.size,
          total: response.data.total,
          pages: response.data.pages
        }
      })
      
      commit('SET_RETRY_COUNT', 0)
      return response
      
    } catch (error) {
      console.error('获取用户列表失败:', error)
      
      // 设置用户友好的错误消息
      let errorMessage = '获取用户列表失败'
      let canRetry = false
      
      if (error.type === 'VALIDATION_ERROR' || error.type === 'DATA_VALIDATION_ERROR') {
        errorMessage = error.message
      } else if (error.type === 'API_ERROR') {
        errorMessage = error.message
        canRetry = error.code >= 500 // 服务器错误可以重试
      } else if (isNetworkError(error)) {
        errorMessage = '网络连接失败，请检查网络连接'
        canRetry = true
      } else if (isServerUnavailable(error)) {
        errorMessage = '服务暂时不可用，请稍后重试'
        canRetry = true
      } else {
        errorMessage = getRetryMessage(error)
        canRetry = true
      }
      
      commit('SET_ERROR', {
        message: errorMessage,
        canRetry,
        originalError: error
      })
      
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async fetchUser({ commit }, id) {
    try {
      const response = await apiGetUserDetail(id)
      
      // 验证API响应结构
      const responseValidation = validateApiResponse(response)
      if (!responseValidation.isValid) {
        const error = new Error(responseValidation.error)
        error.type = 'VALIDATION_ERROR'
        throw error
      }
      
      // 检查响应状态码
      if (response.code !== 200) {
        const errorMessage = getErrorMessage(response.code, response.msg || '获取用户详情失败')
        const error = new Error(errorMessage)
        error.type = 'API_ERROR'
        error.code = response.code
        throw error
      }
      
      // 适配用户详情数据字段
      const adaptedUser = adaptUserData(response.data)
      
      commit('SET_CURRENT_USER', adaptedUser)
      return response
    } catch (error) {
      console.error('获取用户详情失败:', error)
      
      // 生成用户友好的错误消息
      let errorMessage = '获取用户详情失败'
      if (error.type === 'VALIDATION_ERROR' || error.type === 'API_ERROR') {
        errorMessage = error.message
      } else if (isNetworkError(error)) {
        errorMessage = '网络连接失败，请检查网络连接'
      } else if (isServerUnavailable(error)) {
        errorMessage = '服务暂时不可用，请稍后重试'
      }
      
      const enhancedError = new Error(errorMessage)
      enhancedError.originalError = error
      throw enhancedError
    }
  },
  
  async fetchUserProfile(_, userId) {
    try {
      const response = await apiGetUserProfile(userId)
      return response
    } catch (error) {
      console.error('获取用户公开信息失败:', error)
      throw error
    }
  },
  
  async updateUser({ commit }, { id, data }) {
    try {
      const response = await apiUpdateUser(id, data)
      
      // 验证API响应结构
      const responseValidation = validateApiResponse(response)
      if (!responseValidation.isValid) {
        const error = new Error(responseValidation.error)
        error.type = 'VALIDATION_ERROR'
        throw error
      }
      
      // 检查响应状态码
      if (response.code !== 200) {
        const errorMessage = getErrorMessage(response.code, response.msg || '更新用户失败')
        const error = new Error(errorMessage)
        error.type = 'API_ERROR'
        error.code = response.code
        throw error
      }
      
      // 适配更新后的用户数据字段
      const adaptedUser = adaptUserData(response.data)
      
      commit('UPDATE_USER_IN_LIST', adaptedUser)
      commit('SET_CURRENT_USER', adaptedUser)
      
      // 记录操作日志
      Logger.recordUserAction('更新', response.data, data, { success: true })
      
      return response
    } catch (error) {
      console.error('更新用户失败:', error)
      
      // 生成用户友好的错误消息
      let errorMessage = '更新用户失败'
      if (error.type === 'VALIDATION_ERROR' || error.type === 'API_ERROR') {
        errorMessage = error.message
      } else if (isNetworkError(error)) {
        errorMessage = '网络连接失败，请检查网络连接后重试'
      } else if (isServerUnavailable(error)) {
        errorMessage = '服务暂时不可用，请稍后重试'
      }
      
      const enhancedError = new Error(errorMessage)
      enhancedError.originalError = error
      throw enhancedError
    }
  },
  
  async updateUserCreditLimit({ commit }, { id, creditLimit }) {
    try {
      const response = await apiUpdateUserCreditLimit(id, creditLimit)
      
      // 验证API响应结构
      const responseValidation = validateApiResponse(response)
      if (!responseValidation.isValid) {
        const error = new Error(responseValidation.error)
        error.type = 'VALIDATION_ERROR'
        throw error
      }
      
      // 检查响应状态码
      if (response.code !== 200) {
        const errorMessage = getErrorMessage(response.code, response.msg || '更新用户信用额度失败')
        const error = new Error(errorMessage)
        error.type = 'API_ERROR'
        error.code = response.code
        throw error
      }
      
      // 适配更新后的用户数据字段
      const adaptedUser = adaptUserData(response.data)
      
      commit('UPDATE_USER_IN_LIST', adaptedUser)
      commit('SET_CURRENT_USER', adaptedUser)
      
      // 记录操作日志
      Logger.recordUserAction('更新信用额度', response.data, { id, creditLimit }, { success: true })
      
      return response
    } catch (error) {
      console.error('更新用户信用额度失败:', error)
      
      // 生成用户友好的错误消息
      let errorMessage = '更新用户信用额度失败'
      if (error.type === 'VALIDATION_ERROR' || error.type === 'API_ERROR') {
        errorMessage = error.message
      } else if (isNetworkError(error)) {
        errorMessage = '网络连接失败，请检查网络连接后重试'
      } else if (isServerUnavailable(error)) {
        errorMessage = '服务暂时不可用，请稍后重试'
      }
      
      const enhancedError = new Error(errorMessage)
      enhancedError.originalError = error
      throw enhancedError
    }
  },
  
  // eslint-disable-next-line no-unused-vars
  async updateUserStatus({ commit, state }, { id, status, reason }) {
    try {
      const response = await apiUpdateUserStatus(id, { status, reason })
      
      // 验证API响应结构
      const responseValidation = validateApiResponse(response)
      if (!responseValidation.isValid) {
        const error = new Error(responseValidation.error)
        error.type = 'VALIDATION_ERROR'
        throw error
      }
      
      // 检查响应状态码
      if (response.code !== 200) {
        const errorMessage = getErrorMessage(response.code, response.msg || '更新用户状态失败')
        const error = new Error(errorMessage)
        error.type = 'API_ERROR'
        error.code = response.code
        throw error
      }
      
      // 适配更新后的用户数据字段
      const adaptedUser = adaptUserData(response.data)
      
      commit('UPDATE_USER_IN_LIST', adaptedUser)
      
      // 记录操作日志
      const action = status === 'active' ? '启用' : status === 'banned' ? '禁用' : '停用'
      Logger.recordUserAction(action, response.data, { id, status, reason }, { success: true })
      
      return response
    } catch (error) {
      console.error('更新用户状态失败:', error)
      
      // 生成用户友好的错误消息
      let errorMessage = '更新用户状态失败'
      if (error.type === 'VALIDATION_ERROR' || error.type === 'API_ERROR') {
        errorMessage = error.message
      } else if (isNetworkError(error)) {
        errorMessage = '网络连接失败，请检查网络连接后重试'
      } else if (isServerUnavailable(error)) {
        errorMessage = '服务暂时不可用，请稍后重试'
      }
      
      const enhancedError = new Error(errorMessage)
      enhancedError.originalError = error
      throw enhancedError
    }
  },
  
  async deleteUser({ commit, state }, id) {
    try {
      const user = state.userList.find(user => user.id === id)
      const response = await apiDeleteUser(id)
      
      // 验证API响应结构
      const responseValidation = validateApiResponse(response)
      if (!responseValidation.isValid) {
        const error = new Error(responseValidation.error)
        error.type = 'VALIDATION_ERROR'
        throw error
      }
      
      // 检查响应状态码
      if (response.code !== 200) {
        const errorMessage = getErrorMessage(response.code, response.msg || '删除用户失败')
        const error = new Error(errorMessage)
        error.type = 'API_ERROR'
        error.code = response.code
        throw error
      }
      
      commit('REMOVE_USER_FROM_LIST', id)
      
      // 记录操作日志
      if (user) {
        Logger.recordUserAction('删除', user, { id }, response.data)
      }
      
      return response
    } catch (error) {
      console.error('删除用户失败:', error)
      
      // 生成用户友好的错误消息
      let errorMessage = '删除用户失败'
      if (error.type === 'VALIDATION_ERROR' || error.type === 'API_ERROR') {
        errorMessage = error.message
      } else if (isNetworkError(error)) {
        errorMessage = '网络连接失败，请检查网络连接后重试'
      } else if (isServerUnavailable(error)) {
        errorMessage = '服务暂时不可用，请稍后重试'
      }
      
      const enhancedError = new Error(errorMessage)
      enhancedError.originalError = error
      throw enhancedError
    }
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}
